import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Send, X, ChevronRight, ChevronDown, MessageSquare, Sparkles, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// ─── Tree utilities ────────────────────────────────────────────────────────────
function flattenTree(node, result = []) {
  if (!node) return result;
  result.push(node);
  (node.children || []).forEach(c => flattenTree(c, result));
  return result;
}

function countDescendants(node) {
  if (!node || !node.children) return 0;
  return node.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0);
}

// ─── Markdown renderer ─────────────────────────────────────────────────────────
function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)/gm, '• $1')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

// ─── Suggested questions generator ─────────────────────────────────────────────
function getSuggestedQuestions(node) {
  const label = (node?.label || "").toLowerCase();
  
  if (label.includes("scope") || label.includes("work")) {
    return ["What are the main deliverables?", "Are there any exclusions?", "What's the project timeline?"];
  }
  if (label.includes("eligibility") || label.includes("qualification")) {
    return ["What are the minimum requirements?", "What certifications are needed?", "What experience is required?"];
  }
  if (label.includes("commercial") || label.includes("payment")) {
    return ["What are the payment terms?", "Is there advance payment?", "What's the retention percentage?"];
  }
  if (label.includes("liquidated") || label.includes("damages") || label.includes("ld")) {
    return ["What's the daily LD rate?", "Is there a cap on LDs?", "What's the grace period?"];
  }
  if (label.includes("risk")) {
    return ["What are the main risks?", "How can we mitigate these?", "What's the financial exposure?"];
  }
  if (label.includes("insurance")) {
    return ["What coverage is required?", "What are the minimum limits?", "Who arranges the insurance?"];
  }
  
  return ["Tell me more about this section", "What are the key requirements?", "Are there any concerns?"];
}

// ─── Level colors ──────────────────────────────────────────────────────────────
const levelColors = [
  { bg: 'bg-gradient-to-r from-[#1E4E8C]/20 to-[#1E4E8C]/10', border: 'border-[#1E4E8C]/40', text: 'text-[#5790C9]', dot: 'bg-[#1E4E8C]' },
  { bg: 'bg-gradient-to-r from-purple-500/20 to-purple-500/10', border: 'border-purple-500/40', text: 'text-purple-400', dot: 'bg-purple-500' },
  { bg: 'bg-gradient-to-r from-emerald-500/20 to-emerald-500/10', border: 'border-emerald-500/40', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  { bg: 'bg-gradient-to-r from-amber-500/20 to-amber-500/10', border: 'border-amber-500/40', text: 'text-amber-400', dot: 'bg-amber-500' },
  { bg: 'bg-gradient-to-r from-rose-500/20 to-rose-500/10', border: 'border-rose-500/40', text: 'text-rose-400', dot: 'bg-rose-500' },
  { bg: 'bg-gradient-to-r from-cyan-500/20 to-cyan-500/10', border: 'border-cyan-500/40', text: 'text-cyan-400', dot: 'bg-cyan-500' },
];

const getNodeColors = (depth) => levelColors[depth % levelColors.length];

// ─── Main Component ────────────────────────────────────────────────────────────
export function MindMapInteractive({ tenderId }) {
  // Data state
  const [mindmapData, setMindmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tree state
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
  const [selectedNode, setSelectedNode] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  
  // Refs
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // ─── Regenerate mindmap ────────────────────────────────────────────────────────
  const regenerateMindmap = useCallback(async () => {
    try {
      setRegenerating(true);
      toast.info("Regenerating mind map with improved section extraction...");
      
      const response = await api.post(`/tenders/${tenderId}/mindmap/regenerate`);
      
      if (response.data.success) {
        toast.success(`Mind map regenerated with ${response.data.node_count} sections!`);
        
        // Refetch the updated mindmap
        const treeResponse = await api.get(`/tenders/${tenderId}/mindmap/tree`);
        const root = treeResponse.data.root;
        if (root) {
          setExpandedNodes(new Set([root.id || 'root']));
        }
        setMindmapData(root);
      }
    } catch (err) {
      console.error("Failed to regenerate mindmap:", err);
      toast.error(err.response?.data?.detail || "Failed to regenerate mind map");
    } finally {
      setRegenerating(false);
    }
  }, [tenderId]);

  // ─── Fetch mindmap tree on load ──────────────────────────────────────────────
  useEffect(() => {
    async function fetchMindmap() {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/tenders/${tenderId}/mindmap/tree`);
        const root = response.data.root;
        
        // Initialize expanded nodes with root
        if (root) {
          setExpandedNodes(new Set([root.id || 'root']));
        }
        
        setMindmapData(root);
      } catch (err) {
        console.error("Failed to fetch mindmap:", err);
        setError(err.response?.data?.detail || "Failed to load mind map");
      } finally {
        setLoading(false);
      }
    }
    
    if (tenderId) {
      fetchMindmap();
    }
  }, [tenderId]);

  // ─── Scroll chat to bottom ───────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ─── Focus input when chat opens ─────────────────────────────────────────────
  useEffect(() => {
    if (chatOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [chatOpen, selectedNode]);

  // ─── Expand/Collapse handlers ────────────────────────────────────────────────
  const toggleExpand = useCallback((nodeId, e) => {
    e?.stopPropagation();
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (!mindmapData) return;
    const allIds = flattenTree(mindmapData).map(n => n.id);
    setExpandedNodes(new Set(allIds));
  }, [mindmapData]);

  const collapseAll = useCallback(() => {
    if (!mindmapData) return;
    setExpandedNodes(new Set([mindmapData.id]));
  }, [mindmapData]);

  // ─── Node selection and chat ─────────────────────────────────────────────────
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    setChatOpen(true);
    
    // Initialize with node summary as intro message
    const intro = node.summary || 
      (node.key_facts?.length > 0 ? `Key facts about ${node.label}:\n• ${node.key_facts.join('\n• ')}` : 
      `Information about ${node.label}`);
    
    setMessages([{ role: "assistant", content: intro, isIntro: true }]);
  }, []);

  const closeChat = useCallback(() => {
    setChatOpen(false);
    setSelectedNode(null);
    setMessages([]);
    setInput("");
  }, []);

  // ─── Send chat message ───────────────────────────────────────────────────────
  const sendMessage = useCallback(async () => {
    if (!input.trim() || chatLoading || !selectedNode) return;
    
    const userMsg = input.trim();
    setInput("");
    
    // Add user message
    const newMessages = [...messages, { role: "user", content: userMsg }];
    setMessages(newMessages);
    setChatLoading(true);
    
    try {
      // Filter out intro message for API call
      const historyToSend = newMessages
        .filter(m => !m.isIntro)
        .map(m => ({ role: m.role, content: m.content }));
      
      const response = await api.post(`/tenders/${tenderId}/mindmap/chat`, {
        node_id: selectedNode.id,
        messages: historyToSend
      });
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.reply 
      }]);
    } catch (err) {
      console.error("Chat error:", err);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I encountered an error. Please try again.",
        isError: true
      }]);
    } finally {
      setChatLoading(false);
    }
  }, [input, chatLoading, messages, selectedNode, tenderId]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // ─── Render tree node recursively ────────────────────────────────────────────
  const renderTreeNode = useCallback((node, depth = 0, isLast = true) => {
    if (!node) return null;
    
    const nodeId = node.id || `node-${depth}`;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(nodeId);
    const isSelected = selectedNode?.id === nodeId;
    const colors = getNodeColors(depth);
    const descendantCount = countDescendants(node);
    
    return (
      <div key={nodeId} className="relative" data-testid={`mindmap-node-${nodeId}`}>
        {/* Vertical connector line */}
        {depth > 0 && (
          <div 
            className="absolute left-[11px] top-0 w-0.5 bg-gradient-to-b from-slate-600/50 to-transparent"
            style={{ height: isLast ? '20px' : '100%' }}
          />
        )}
        
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2, delay: Math.min(depth * 0.03, 0.3) }}
          className="relative"
          style={{ marginLeft: depth > 0 ? '24px' : '0' }}
        >
          {/* Horizontal connector */}
          {depth > 0 && (
            <div className="absolute left-[-13px] top-[20px] w-3 h-0.5 bg-slate-600/50" />
          )}
          
          {/* Node card */}
          <div
            className={`
              relative rounded-lg border transition-all duration-200 cursor-pointer mb-2
              ${colors.bg} ${colors.border}
              ${isSelected ? 'ring-2 ring-[#1E4E8C]/60 shadow-lg shadow-[#1E4E8C]/20' : 'hover:shadow-md hover:border-opacity-60'}
            `}
            onClick={() => handleNodeClick(node)}
          >
            <div className="p-3">
              <div className="flex items-start gap-3">
                {/* Expand/Collapse button */}
                {hasChildren && (
                  <button
                    onClick={(e) => toggleExpand(nodeId, e)}
                    className={`mt-0.5 p-1 rounded hover:bg-white/10 transition-colors ${colors.text}`}
                    data-testid={`expand-btn-${nodeId}`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                
                {/* No children spacer */}
                {!hasChildren && <div className="w-6" />}
                
                {/* Emoji icon */}
                {node.emoji && (
                  <span className="text-xl mt-0.5 flex-shrink-0">{node.emoji}</span>
                )}
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold ${colors.text}`}>
                      {node.label}
                    </span>
                    {hasChildren && !isExpanded && (
                      <span className="text-xs text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded">
                        {descendantCount} hidden
                      </span>
                    )}
                    {isSelected && (
                      <MessageSquare className="w-3.5 h-3.5 text-[#1E4E8C]" />
                    )}
                  </div>
                  
                  {/* Summary preview */}
                  {node.summary && (
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                      {node.summary}
                    </p>
                  )}
                  
                  {/* Key facts (shown when selected) */}
                  <AnimatePresence>
                    {isSelected && node.key_facts && node.key_facts.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2 pt-2 border-t border-white/10"
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <Sparkles className="w-3 h-3 text-amber-400" />
                          <span className="text-xs text-amber-400 font-medium">Key Facts</span>
                        </div>
                        <ul className="space-y-1">
                          {node.key_facts.slice(0, 4).map((fact, i) => (
                            <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                              <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} mt-1.5 flex-shrink-0`} />
                              {fact}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
          
          {/* Children */}
          <AnimatePresence>
            {hasChildren && isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                {node.children.map((child, index) => 
                  renderTreeNode(child, depth + 1, index === node.children.length - 1)
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }, [expandedNodes, selectedNode, handleNodeClick, toggleExpand]);

  // ─── Loading state ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-[#0b0f1a] rounded-xl border border-[#3A3F4A]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E4E8C] mx-auto mb-3" />
          <p className="text-slate-400">Loading mind map...</p>
        </div>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-[#0b0f1a] rounded-xl border border-[#3A3F4A]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-red-400 mb-2">{error}</p>
          <p className="text-xs text-slate-500">Upload and analyze a document to generate the mind map</p>
        </div>
      </div>
    );
  }

  // ─── No data state ───────────────────────────────────────────────────────────
  if (!mindmapData) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-[#0b0f1a] rounded-xl border border-[#3A3F4A]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#1E4E8C] to-[#143A6B] flex items-center justify-center">
            <span className="text-2xl">🗺️</span>
          </div>
          <p className="text-slate-400">Upload and analyze a document to generate the mind map</p>
        </div>
      </div>
    );
  }

  const suggestedQuestions = selectedNode ? getSuggestedQuestions(selectedNode) : [];

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div 
      className="relative bg-[#0b0f1a] rounded-xl border border-[#3A3F4A] overflow-hidden"
      style={{ minHeight: "600px" }}
      data-testid="mindmap-interactive"
    >
      {/* Main content area */}
      <div className="flex h-full" style={{ minHeight: "600px" }}>
        
        {/* Tree View Panel */}
        <div 
          className={`flex flex-col transition-all duration-300 ${chatOpen ? 'w-1/2' : 'w-full'}`}
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-[#0f172a] to-[#1e1b4b] border-b border-[#1E4E8C]/25 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6366f1] to-[#143A6B] flex items-center justify-center shadow-lg shadow-[#6366f1]/30">
                <span className="text-lg">🗺️</span>
              </div>
              <div>
                <h2 className="text-sm font-bold text-white">{mindmapData.label || "Tender Mind Map"}</h2>
                <p className="text-xs text-[#6366f1]">
                  Click a node to chat • {flattenTree(mindmapData).length} topics
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={regenerateMindmap}
                disabled={regenerating}
                className="px-3 py-1.5 text-xs bg-[#1E4E8C]/10 border border-[#1E4E8C]/30 text-[#5790C9] rounded-md hover:bg-[#1E4E8C]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="regenerate-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
              <button
                onClick={expandAll}
                className="px-3 py-1.5 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-md hover:bg-emerald-500/20 transition-colors"
                data-testid="expand-all-btn"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1.5 text-xs bg-red-500/10 border border-red-500/30 text-red-400 rounded-md hover:bg-red-500/20 transition-colors"
                data-testid="collapse-all-btn"
              >
                Collapse All
              </button>
            </div>
          </div>
          
          {/* Tree content */}
          <div className="flex-1 overflow-auto p-6">
            {renderTreeNode(mindmapData)}
          </div>
        </div>
        
        {/* Chat Panel */}
        <AnimatePresence>
          {chatOpen && selectedNode && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "50%", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="h-full bg-[#0f1629] border-l border-[#1E4E8C]/25 flex flex-col"
              style={{ minHeight: "600px" }}
              data-testid="chat-panel"
            >
              {/* Chat Header */}
              <div className="p-4 border-b border-[#1E4E8C]/20 bg-gradient-to-r from-[#0f172a]/90 to-[#1e1b4b]/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                      style={{ 
                        background: `linear-gradient(135deg, ${selectedNode.color || '#6366f1'}, ${selectedNode.color || '#6366f1'}aa)`,
                        boxShadow: `0 0 12px ${selectedNode.color || '#6366f1'}44`
                      }}
                    >
                      {selectedNode.emoji || '📋'}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white">{selectedNode.label}</h3>
                      <p className="text-xs text-[#6366f1]">AI context loaded</p>
                    </div>
                  </div>
                  <button
                    onClick={closeChat}
                    className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                    data-testid="close-chat-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="flex items-center gap-2 mb-1.5">
                        <div 
                          className="w-5 h-5 rounded flex items-center justify-center text-xs"
                          style={{ background: `linear-gradient(135deg, ${selectedNode.color || '#6366f1'}, ${selectedNode.color || '#6366f1'}aa)` }}
                        >
                          {selectedNode.emoji || '📋'}
                        </div>
                        <span className="text-[10px] text-slate-500 font-semibold uppercase">
                          {msg.isIntro ? "Section Overview" : "AI Response"}
                        </span>
                      </div>
                    )}
                    <div
                      className={`max-w-[90%] rounded-2xl p-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "rounded-tr-sm text-white"
                          : msg.isError
                            ? "rounded-tl-sm bg-red-500/10 border border-red-500/20"
                            : "rounded-tl-sm"
                      }`}
                      style={
                        msg.role === "user"
                          ? { 
                              background: `linear-gradient(135deg, ${selectedNode.color || '#6366f1'}dd, ${selectedNode.color || '#6366f1'}aa)`,
                              boxShadow: `0 4px 16px ${selectedNode.color || '#6366f1'}44`
                            }
                          : msg.isIntro
                            ? { 
                                background: "linear-gradient(135deg, rgba(30,27,75,.8), rgba(15,22,41,.9))",
                                border: `1px solid ${selectedNode.color || '#6366f1'}44`
                              }
                            : { 
                                background: "rgba(30,41,59,.7)",
                                border: "1px solid rgba(99,102,241,.15)"
                              }
                      }
                    >
                      {msg.isIntro && (
                        <div 
                          className="text-[10px] font-bold uppercase tracking-wider mb-2"
                          style={{ color: selectedNode.color || '#6366f1' }}
                        >
                          📋 Section Overview
                        </div>
                      )}
                      <div 
                        className="text-slate-300"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                      />
                    </div>
                  </div>
                ))}

                {chatLoading && (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-5 h-5 rounded flex items-center justify-center text-xs"
                      style={{ background: `linear-gradient(135deg, ${selectedNode.color || '#6366f1'}, ${selectedNode.color || '#6366f1'}aa)` }}
                    >
                      {selectedNode.emoji || '📋'}
                    </div>
                    <div className="flex gap-1 px-4 py-2 bg-slate-800/50 rounded-2xl rounded-tl-sm border border-[#1E4E8C]/15">
                      {[0, 1, 2].map(d => (
                        <div
                          key={d}
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ 
                            backgroundColor: selectedNode.color || '#6366f1',
                            animationDelay: `${d * 0.2}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Suggested questions */}
              {messages.length === 1 && suggestedQuestions.length > 0 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#1E4E8C]/10 border border-[#1E4E8C]/25 text-[#a5b4fc] hover:bg-[#1E4E8C]/20 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t border-[#1E4E8C]/20 bg-[#0f172a]/80">
                <div className="flex gap-2 items-end bg-slate-800/60 border border-[#1E4E8C]/25 rounded-xl px-3 py-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Ask about ${selectedNode.label}...`}
                    rows={1}
                    className="flex-1 bg-transparent border-none outline-none text-sm text-slate-200 resize-none max-h-20 overflow-auto placeholder:text-slate-500"
                    onInput={(e) => {
                      e.target.style.height = "auto";
                      e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
                    }}
                    data-testid="chat-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || chatLoading}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all flex-shrink-0"
                    style={{
                      background: input.trim() && !chatLoading
                        ? `linear-gradient(135deg, ${selectedNode.color || '#6366f1'}, ${selectedNode.color || '#6366f1'}bb)`
                        : "rgba(99,102,241,.15)",
                      color: input.trim() && !chatLoading ? "#fff" : "#4b5563",
                      cursor: input.trim() && !chatLoading ? "pointer" : "not-allowed",
                      boxShadow: input.trim() && !chatLoading ? `0 4px 12px ${selectedNode.color || '#6366f1'}55` : "none",
                    }}
                    data-testid="send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-[10px] text-slate-600 text-center mt-2">
                  Enter to send · Shift+Enter for new line
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default MindMapInteractive;
